import { Matrix4 } from 'three';

/**
 * Standing a 3D prop exactly where the map says its cell is.
 *
 * The map has no camera to borrow. It warps the grid rectangle onto four authored
 * corners by BILINEAR interpolation, which is not a perspective projection: no
 * camera pose reproduces it, and neither does a homography through the grid's
 * corners. Fitting one anyway agrees at the four corners and bows away from the map
 * in between — measured on a 20x20 board, up to 21px across and 86px down, which at
 * an 85px cell is a whole cell of drift right through the middle of the board.
 *
 * So there is no global projection here at all. Each prop is placed from the four
 * morphed corners of ITS OWN cell block — the same four points the map draws that
 * block with — and a quad that small is projective to well under a pixel. The prop
 * cannot drift from its cell, because it is built out of the cell.
 *
 * That puts the perspective divide in each object's own matrix rather than in the
 * camera's. The camera is then only the screen-pixels-to-clip-space conversion, and
 * it passes w through untouched.
 */

/**
 * Homography taking the unit square to four screen points, corners clockwise from
 * the top-left. Returns rows of a 3x3, or null if the quad is degenerate.
 */
function unitSquareTo(p0, p1, p2, p3) {
  const affine = () => [
    p1.x - p0.x, p3.x - p0.x, p0.x,
    p1.y - p0.y, p3.y - p0.y, p0.y,
    0, 0, 1,
  ];

  const sx = p0.x - p1.x + p2.x - p3.x;
  const sy = p0.y - p1.y + p2.y - p3.y;
  if (Math.abs(sx) < 1e-9 && Math.abs(sy) < 1e-9) return affine();

  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const den = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(den) < 1e-9) return affine();

  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;
  return [
    p1.x - p0.x + g * p1.x, p3.x - p0.x + h * p3.x, p0.x,
    p1.y - p0.y + g * p1.y, p3.y - p0.y + h * p3.y, p0.y,
    g, h, 1,
  ];
}

/**
 * The matrix that stands an object on one cell block.
 *
 * Object space is the block's own: x east and z south, each spanning -span/2 to
 * +span/2, y up in the same units, and the origin on the floor at the block's
 * centre. That is exactly the space `ModelLoaderService.seat` leaves a prop in, so a
 * seated model needs nothing but this matrix to land on its cell.
 *
 * @param quad  the block's four morphed corners in CSS pixels, as the map draws them
 * @param span  side of the block, in cells
 * @param heightScale  screen pixels per unit of height at this block, before the
 *                     divide — the block's own on-screen cell width, so a prop one
 *                     cell tall reads as one cell tall wherever it stands
 * @returns a Matrix4 producing homogeneous SCREEN coordinates, or null
 */
export function blockMatrix(quad, span = 1, heightScale = 1) {
  if (!quad) return null;
  const { topLeft, topRight, bottomRight, bottomLeft } = quad;
  const pts = [topLeft, topRight, bottomRight, bottomLeft];
  if (pts.some(p => !p || !Number.isFinite(p.x) || !Number.isFinite(p.y))) return null;

  const h = unitSquareTo(topLeft, topRight, bottomRight, bottomLeft);
  if (!h || h.some(n => !Number.isFinite(n))) return null;

  // The homography is written over the unit square; object space is the block,
  // centred. u = x/span + 1/2 and v = z/span + 1/2 folds both conversions in.
  const side = Math.max(1, span);
  const s = 1 / side;
  const a = h[0] * s, b = h[1] * s;
  const d = h[3] * s, e = h[4] * s;
  const g = h[6] * s, i = h[7] * s;
  // What the half-cell shift contributes to each row's constant term.
  const c = h[0] * 0.5 + h[1] * 0.5 + h[2];
  const f = h[3] * 0.5 + h[4] * 0.5 + h[5];
  const w0 = h[6] * 0.5 + h[7] * 0.5 + 1;

  /*
   * X = a*x + b*z + c
   * Y = d*x + e*z + f - heightScale*y      screen y runs down, so height subtracts
   * Z = 0                                   flat props on one floor; order is stated
   * W = g*x + i*z + w0
   */
  const matrix = new Matrix4();
  matrix.set(
    a, 0, b, c,
    d, -heightScale, e, f,
    0, 0, 0, 0,
    g, 0, i, w0
  );
  return matrix;
}

/**
 * The camera: homogeneous screen pixels to clip space, with w passed through.
 *
 * All the projection has already happened in each object's own matrix, so there is
 * nothing left for this to do but flip y and rescale into the [-1, 1] box. Depth is
 * left flat — these are props standing on one floor, drawn in a stated order.
 */
export function screenProjection(viewport) {
  if (!viewport || !(viewport.width > 0) || !(viewport.height > 0)) return null;

  const matrix = new Matrix4();
  matrix.set(
    2 / viewport.width, 0, 0, -1,
    0, -2 / viewport.height, 0, 1,
    0, 0, 0, 0,
    0, 0, 0, 1
  );
  return matrix;
}
